/**
 * Lazy-instantiated singleton wrapper around the SHA-256 web worker.
 * Falls back to main-thread hashing if Workers are unavailable.
 */

let worker: Worker | null = null;
const pending = new Map<string, (sha256: string | undefined) => void>();

function getWorker(): Worker | null {
  if (worker) return worker;
  try {
    worker = new Worker(new URL("./sha256Worker.ts", import.meta.url), { type: "module" });
    worker.addEventListener("message", (e: MessageEvent) => {
      const { id, sha256, error } = e.data as { id: string; sha256?: string; error?: string };
      const cb = pending.get(id);
      if (cb) {
        pending.delete(id);
        if (error) {
          console.warn("[sha256Worker] error:", error);
          cb(undefined);
        } else {
          cb(sha256);
        }
      }
    });
    worker.addEventListener("error", (e) => {
      console.warn("[sha256Worker] terminated:", e.message);
      worker = null;
      // Reject all pending
      for (const cb of pending.values()) cb(undefined);
      pending.clear();
    });
  } catch (e) {
    console.warn("[hashClient] worker unavailable, falling back to main thread:", e);
    worker = null;
  }
  return worker;
}

export async function hashBlobInWorker(blob: Blob): Promise<string | undefined> {
  if (!crypto?.subtle) return undefined;
  const w = getWorker();
  if (!w) {
    // Fallback — main thread.
    try {
      const buf = await blob.arrayBuffer();
      const digest = await crypto.subtle.digest("SHA-256", buf);
      const bytes = new Uint8Array(digest);
      let hex = "";
      for (let i = 0; i < bytes.length; i++) hex += bytes[i].toString(16).padStart(2, "0");
      return hex;
    } catch {
      return undefined;
    }
  }
  return new Promise<string | undefined>((resolve) => {
    const id = crypto.randomUUID();
    pending.set(id, resolve);
    w.postMessage({ id, blob });
  });
}
